import { logReportDownload } from '../../util/auditLogger';
import React, { useState, useEffect, useRef, useMemo } from "react";
import Context from "../../Context";
import { Header, Loading, ScheduledTasksWidget } from "../../components/modules";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components";
import {
  FiRefreshCw,
  FiTrendingUp,
  FiShoppingBag,
  FiCheckCircle,
  FiClock,
  FiAlertCircle,
  FiXCircle,
  FiActivity,
  FiDollarSign,
  FiGlobe,
  FiMessageSquare,
  FiLayers,
  FiTag,
  FiAward,
  FiCalendar,
  FiUsers,
  FiDownload,
} from "react-icons/fi";
import { RiFileList3Line, RiCoupon2Line } from "react-icons/ri";
import { LastTwoDigitsStockPanel } from "./components/LastTwoDigitsStockPanel";
import { LotteryBlockSetsPanel } from "./components/LotteryBlockSetsPanel";
import { ResponsiveContainer, BarChart, Bar, Tooltip } from "recharts";
import * as xlsx from "xlsx";
import dayjs from "dayjs";
import { doc, onSnapshot } from "firebase/firestore";
import { roundDateToDate } from "../../util";

const STATUS_CONFIG = {
  approved: {
    label: "อนุมัติแล้ว",
    subLabel: "อนุมัติเรียบร้อย",
    color: "#10b981", // emerald-500
    textColor: "text-emerald-600",
    lightBg: "bg-emerald-50",
    border: "border-emerald-200",
    icon: FiCheckCircle,
    badgeColor: "bg-emerald-100 text-emerald-800 border-emerald-300",
  },
  paid: {
    label: "ชำระแล้ว",
    subLabel: "รอตรวจสอบสลิป",
    color: "#0ea5e9", // sky-500
    textColor: "text-sky-600",
    lightBg: "bg-sky-50",
    border: "border-sky-200",
    icon: FiClock,
    badgeColor: "bg-sky-100 text-sky-800 border-sky-300",
  },
  pending: {
    label: "รอชำระเงิน",
    subLabel: "รอการโอนเงิน",
    color: "#f59e0b", // amber-500
    textColor: "text-amber-600",
    lightBg: "bg-amber-50",
    border: "border-amber-200",
    icon: FiAlertCircle,
    badgeColor: "bg-amber-100 text-amber-800 border-amber-300",
  },
  canceled: {
    label: "ยกเลิก",
    subLabel: "ยกเลิกคำสั่งซื้อ",
    color: "#f43f5e", // rose-500
    textColor: "text-rose-600",
    lightBg: "bg-rose-50",
    border: "border-rose-200",
    icon: FiXCircle,
    badgeColor: "bg-rose-100 text-rose-800 border-rose-300",
  },
};

const COUPON_TYPE_CONFIG = {
  voucher: { label: "คูปองสลากฟรี", color: "#10b981", bg: "bg-emerald-50", text: "text-emerald-700", badge: "bg-emerald-100 text-emerald-800" },
  discount: { label: "คูปองส่วนลด", color: "#f59e0b", bg: "bg-amber-50", text: "text-amber-700", badge: "bg-amber-100 text-amber-800" },
  reserve: { label: "คูปองสิทธิ์จอง", color: "#3b82f6", bg: "bg-blue-50", text: "text-blue-700", badge: "bg-blue-100 text-blue-800" },
  reserve100: { label: "คูปองจองชุด 100 ใบ", color: "#6366f1", bg: "bg-indigo-50", text: "text-indigo-700", badge: "bg-indigo-100 text-indigo-800" },
  "voucher-lock": { label: "คูปองล็อคเลข", color: "#ec4899", bg: "bg-pink-50", text: "text-pink-700", badge: "bg-pink-100 text-pink-800" },
  vip: { label: "คูปอง VIP", color: "#8b5cf6", bg: "bg-purple-50", text: "text-purple-700", badge: "bg-purple-100 text-purple-800" },
  other: { label: "คูปองอื่นๆ", color: "#64748b", bg: "bg-slate-50", text: "text-slate-700", badge: "bg-slate-100 text-slate-800" },
};

const CHANNEL_COLORS = {
  web: "#3b82f6", // blue-500
  lao: "#6366f1", // indigo-500
  line: "#06C755", // line green
};

const INVENTORY_COLORS = {
  sold: "#10b981", // emerald
  cart: "#f97316", // orange
  remaining: "#64748b", // slate
};

const DELIVERY_CHANNEL_CONFIG = {
  online: { label: "ฝากสลากในระบบ", color: "#10b981", subLabel: "Online" },
  mail: { label: "จัดส่งไปรษณีย์", color: "#f97316", subLabel: "Mail" },
  offline: { label: "รับเองหน้าร้าน", color: "#6366f1", subLabel: "Offline" },
};

// Smooth Visible Rolling Counter Component
const AnimatedNumber = ({ value = 0, prefix = "", suffix = "", duration = 1000, className = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isChanged, setIsChanged] = useState(false);
  const currentValRef = useRef(0);
  const isFirstRender = useRef(true);

  useEffect(() => {
    const end = Number(value) || 0;
    const start = currentValRef.current;

    if (start === end && !isFirstRender.current) return;
    isFirstRender.current = false;

    setIsChanged(true);
    const animDuration = start === 0 ? Math.min(duration, 1200) : 800;
    const startTime = performance.now();

    let animationFrameId;

    const updateCounter = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / animDuration, 1);
      // easeOutExpo for smooth deceleration
      const ease = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      const current = Math.round(start + (end - start) * ease);

      setDisplayValue(current);
      currentValRef.current = current;

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(updateCounter);
      } else {
        setDisplayValue(end);
        currentValRef.current = end;
      }
    };

    animationFrameId = requestAnimationFrame(updateCounter);

    const timer = setTimeout(() => {
      setIsChanged(false);
    }, animDuration + 400);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      clearTimeout(timer);
    };
  }, [value, duration]);

  return (
    <span
      className={`transition-all duration-300 inline-block font-mono ${isChanged ? "scale-105" : ""
        } ${className}`}
    >
      {prefix}
      {Number(displayValue).toLocaleString()}
      {suffix}
    </span>
  );
};

export const RealtimeOrderDashboard = () => {
  const { db, configs, callApi } = Context();
  const [roundDate, setRoundDate] = useState(configs?.roundDate || "");
  const [stats, setStats] = useState(null);
  const [rawDashboardData, setRawDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [listenerError, setListenerError] = useState("");

  // Sync initial roundDate from configs
  useEffect(() => {
    if (configs?.roundDate && !roundDate) {
      setRoundDate(configs.roundDate);
    }
  }, [configs?.roundDate, roundDate]);

  // Keep the selected round's summary live; detach on round change or unmount.
  useEffect(() => {
    if (!roundDate || !db) return;

    setLoading(true);
    setListenerError("");
    setStats(null);
    setRawDashboardData(null);
    setLastUpdated(null);
    return onSnapshot(doc(db, "Dashboard", roundDate), (docSnap) => {

      if (docSnap.exists()) {
        const data = docSnap.data();
        setRawDashboardData(data);
        const rawStats = data.realtimeOrderStats;

        const laoFallback = {
          orders: data.laoOrder || 0,
          tickets: data.laoLotto || 0,
          amountLak: data.laoAmountLak || 0,
          amountThb: data.laoAmountThb || 0,
          approvedOrders: data.laoApprovedOrder || 0,
          approvedTickets: data.laoApprovedLotto || 0,
          approvedAmountLak: data.laoApprovedAmountLak || 0,
          approvedAmountThb: data.laoApprovedAmountThb || 0,
          paidOrders: data.laoPaidOrder || 0,
          paidTickets: data.laoPaidLotto || 0,
          paidAmountLak: data.laoPaidAmountLak || 0,
          pendingOrders: data.laoPendingOrder || 0,
          pendingTickets: data.laoPendingLotto || 0,
          pendingAmountLak: data.laoPendingAmountLak || 0,
          canceledOrders: data.laoCanceledOrder || 0,
          canceledTickets: data.laoCanceledLotto || 0,
          canceledAmountLak: data.laoCanceledAmountLak || 0,
        };

        const lineFallback = {
          orders: data.lineOrder || 0,
          tickets: data.lineLotto || 0,
          amount: data.lineAmount || 0,
          approvedOrders: data.lineApprovedOrder || 0,
          approvedTickets: data.lineApprovedLotto || 0,
          approvedAmount: data.lineApprovedAmount || 0,
          paidOrders: data.linePaidOrder || 0,
          paidTickets: data.linePaidLotto || 0,
          paidAmount: data.linePaidAmount || 0,
          pendingOrders: data.linePendingOrder || 0,
          pendingTickets: data.linePendingLotto || 0,
          pendingAmount: data.linePendingAmount || 0,
          canceledOrders: data.lineCanceledOrder || 0,
          canceledTickets: data.lineCanceledLotto || 0,
          canceledAmount: data.lineCanceledAmount || 0,
        };

        const sanitizeStatus = (st) => ({
          orders: Math.max(0, Number(st?.orders) || 0),
          tickets: Math.max(0, Number(st?.tickets) || 0),
          amount: Math.max(0, Number(st?.amount) || 0),
        });

        let resolvedStats = null;
        if (rawStats) {
          resolvedStats = {
            ...rawStats,
            approved: sanitizeStatus(rawStats.approved),
            paid: sanitizeStatus(rawStats.paid),
            pending: sanitizeStatus(rawStats.pending),
            canceled: sanitizeStatus(rawStats.canceled),
            total: sanitizeStatus(rawStats.total),
            lao: rawStats.lao || laoFallback,
            line: rawStats.line || lineFallback,
          };
          setStats(resolvedStats);
          setLastUpdated(rawStats.updatedAt ? new Date(rawStats.updatedAt) : new Date());
        } else {
          // Fallback from legacy dashboard structure if realtimeOrderStats not yet initialized
          resolvedStats = {
            approved: {
              orders: data.currentOrderApproved || 0,
              tickets: data.currentLottoSold || 0,
              amount: data.totalIncome || 0,
            },
            paid: {
              orders: data.currentOrderPaid || 0,
              tickets: 0,
              amount: 0,
            },
            pending: {
              orders: data.currentOrderPending || 0,
              tickets: 0,
              amount: 0,
            },
            canceled: {
              orders: data.currentOrderCanceled || 0,
              tickets: 0,
              amount: 0,
            },
            total: {
              orders: data.currentOrderAll || 0,
              tickets: data.currentLotto || 0,
              amount: data.totalIncome || 0,
            },
            lao: laoFallback,
            line: lineFallback,
            updatedAt: data.lastupdate || Date.now(),
          };
          setStats(resolvedStats);
          setLastUpdated(data.lastupdate ? new Date(data.lastupdate) : new Date());
        }
      } else {
        setStats(null);
        setRawDashboardData(null);
        setLastUpdated(null);
      }
      setLoading(false);
    }, (err) => {
      console.error("Dashboard listener error:", err);
      setListenerError("การเชื่อมต่อข้อมูลเรียลไทม์ขัดข้อง กรุณาโหลดหน้าใหม่เพื่อลองอีกครั้ง");
      setLoading(false);
    });
  }, [roundDate, db]);

  // Manual Re-sync / Recalculate
  const handleRecalculate = async () => {
    if (!roundDate) return;
    setSyncing(true);
    try {
      await callApi(
        "dashboard-recalculateRealtimeOrderStats",
        { roundDate },
        { notShowLoading: true },
      );
      // The listener receives the recalculated summary from Firestore.
    } catch (e) {
      console.error("Failed to recalculate order stats:", e);
    } finally {
      setSyncing(false);
    }
  };

  const totalOrders = stats?.total?.orders || 0;
  const totalTickets = stats?.total?.tickets || 0;
  const totalAmount = stats?.total?.amount || 0;
  const approvedOrders = stats?.approved?.orders || 0;
  const approvedTickets = stats?.approved?.tickets || 0;
  const approvedAmount = stats?.approved?.amount || 0;
  const paidOrders = stats?.paid?.orders || 0;
  const paidTickets = stats?.paid?.tickets || 0;
  const paidAmount = stats?.paid?.amount || 0;
  const pendingOrders = stats?.pending?.orders || 0;
  const pendingTickets = stats?.pending?.tickets || 0;
  const pendingAmount = stats?.pending?.amount || 0;
  const canceledOrders = stats?.canceled?.orders || 0;
  const canceledTickets = stats?.canceled?.tickets || 0;
  const canceledAmount = stats?.canceled?.amount || 0;

  // Ticket Inventory (Total, On-Block, Off-Block, Sold, Remaining, Users)
  const lottoTotal = Number(rawDashboardData?.currentLotto || 0);
  const lottoSold = Number(rawDashboardData?.currentLottoSold || approvedTickets || 0);
  const lottoOnBlock = Number(rawDashboardData?.currentLottoOnBlock || 0);
  const lottoOffBlock = Number(
    rawDashboardData?.currentLottoOffBlock !== undefined
      ? rawDashboardData?.currentLottoOffBlock
      : Math.max(0, lottoTotal - lottoSold - lottoOnBlock)
  );
  const lottoInCart = Number(rawDashboardData?.currentLottoInCart || 0);
  const lottoRemaining = Math.max(0, lottoTotal - lottoSold);
  const totalUsers = Number(rawDashboardData?.currentUser || 0);

  // Lao statistics
  const laoOrders = stats?.lao?.orders || 0;
  const laoTickets = stats?.lao?.tickets || 0;
  const laoAmountLak = stats?.lao?.amountLak || 0;
  const laoAmountThb = stats?.lao?.amountThb || 0;
  const laoApprovedOrders = stats?.lao?.approvedOrders || 0;
  const laoApprovedTickets = stats?.lao?.approvedTickets || 0;
  const laoApprovedAmountLak = stats?.lao?.approvedAmountLak || 0;
  const laoApprovedAmountThb = stats?.lao?.approvedAmountThb || 0;

  // LINE app statistics
  const lineOrders = stats?.line?.orders || 0;
  const lineTickets = stats?.line?.tickets || 0;
  const lineAmount = stats?.line?.amount || 0;
  const lineApprovedOrders = stats?.line?.approvedOrders || 0;
  const lineApprovedTickets = stats?.line?.approvedTickets || 0;
  const lineApprovedAmount = stats?.line?.approvedAmount || 0;

  // Web / Other Direct Orders
  const webOrders = Math.max(0, totalOrders - laoOrders - lineOrders);
  const webTickets = Math.max(0, totalTickets - laoTickets - lineTickets);
  const webApprovedOrders = Math.max(0, approvedOrders - laoApprovedOrders - lineApprovedOrders);
  const webApprovedTickets = Math.max(0, approvedTickets - laoApprovedTickets - lineApprovedTickets);
  const webAmount = Math.max(0, totalAmount - laoAmountThb - lineAmount);
  const webApprovedAmount = Math.max(0, approvedAmount - laoApprovedAmountThb - lineApprovedAmount);

  const calculatePercent = (val, total) => {
    const numVal = Number(val) || 0;
    const numTotal = Number(total) || 0;
    if (numTotal <= 0 || numVal <= 0) return "0.0%";
    return `${((numVal / numTotal) * 100).toFixed(1)}%`;
  };

  // ─── Sales Trend & Totals Data Processing ─────────────────────────────────

  // 2. Daily Sales & Tickets Trend Data
  const salesTrendData = useMemo(() => {
    if (!rawDashboardData?.historySale) return [];
    return Object.entries(rawDashboardData.historySale)
      .map(([dateKey, val]) => ({
        date: dateKey.length > 5 ? dateKey.slice(5) : dateKey,
        fullDate: dateKey,
        income: Number(val?.income || 0),
        tickets: Number(val?.size || 0),
        orders: Number(val?.orderCount || 0),
      }))
      .sort((a, b) => a.fullDate.localeCompare(b.fullDate));
  }, [rawDashboardData]);

  // 3. Daily Sales Totals Summary
  const dailySalesTotals = useMemo(() => {
    return salesTrendData.reduce(
      (acc, item) => ({
        orders: acc.orders + item.orders,
        tickets: acc.tickets + item.tickets,
        income: acc.income + item.income,
      }),
      { orders: 0, tickets: 0, income: 0 }
    );
  }, [salesTrendData]);

  // Max daily income for peak day highlight
  const maxDailyIncome = useMemo(() => {
    if (!salesTrendData || salesTrendData.length === 0) return 0;
    return Math.max(...salesTrendData.map((d) => d.income));
  }, [salesTrendData]);

  // 4. 30 Days New Users Data Processing
  const newUsers30DaysData = useMemo(() => {
    if (!rawDashboardData?.historyNewUser) return [];
    return Object.entries(rawDashboardData.historyNewUser)
      .map(([dateKey, val]) => ({
        date: dateKey.length > 5 ? dateKey.slice(5) : dateKey,
        fullDate: dateKey,
        count: typeof val === "object" ? Number(val.size || val.count || 0) : Number(val || 0),
      }))
      .sort((a, b) => a.fullDate.localeCompare(b.fullDate));
  }, [rawDashboardData]);

  const newUsers30DaysTotals = useMemo(() => {
    return newUsers30DaysData.reduce(
      (acc, item) => ({
        count: acc.count + item.count,
      }),
      { count: 0 }
    );
  }, [newUsers30DaysData]);

  const maxDailyNewUsers = useMemo(() => {
    if (!newUsers30DaysData || newUsers30DaysData.length === 0) return 0;
    return Math.max(...newUsers30DaysData.map((d) => d.count));
  }, [newUsers30DaysData]);

  // Format date helper for Thai display
  const formatThaiDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const parts = dateStr.split("-");
      if (parts.length === 3) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
        if (!isNaN(d.getTime())) {
          return d.toLocaleDateString("th-TH", {
            day: "numeric",
            month: "short",
            year: "numeric",
            weekday: "short",
          });
        }
      }
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) {
        return d.toLocaleDateString("th-TH", {
          day: "numeric",
          month: "short",
          year: "numeric",
          weekday: "short",
        });
      }
      return dateStr;
    } catch {
      return dateStr;
    }
  };

  // Export Daily Sales Summary Table to Excel (.xlsx)
  const exportDailySalesToXLSX = () => {
    if (!salesTrendData || salesTrendData.length === 0) return;

    const now = new Date();
    const thaiExportDate = now.toLocaleDateString("th-TH", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });

    const fileDate = dayjs(now).format("DD-MM-YYYY");
    const fileTime = dayjs(now).format("HH.mm.ss");
    const roundDisplay = roundDate
      ? roundDateToDate(roundDate)
      : configs?.roundDate
        ? roundDateToDate(configs.roundDate)
        : "-";

    // Title / Metadata Header Rows in the Excel file
    const metaRows = [
      ["รายงานสรุปยอดขายรายวัน"],
      [`งวดประจำวันที่: ${roundDisplay}`],
      [`วันและเวลาที่ออกรายงาน: ${thaiExportDate} น.`],
      [], // blank line
    ];

    const headers = [
      "วันที่ทำรายการ",
      "วันที่ (ค.ศ.)",
      "จำนวนคำสั่งซื้อ (รายการ)",
      "จำนวนสลาก (ใบ)",
      "ยอดขายรวม (บาท)",
      "สัดส่วนยอดขาย (%)",
      "เฉลี่ยต่อคำสั่งซื้อ (บาท)",
    ];

    const rows = salesTrendData.map((item) => {
      const percent =
        dailySalesTotals.income > 0
          ? ((item.income / dailySalesTotals.income) * 100).toFixed(1)
          : "0.0";
      const avgPerOrder =
        item.orders > 0 ? Math.round(item.income / item.orders) : 0;

      return [
        formatThaiDate(item.fullDate),
        item.fullDate,
        item.orders,
        item.tickets,
        item.income,
        `${percent}%`,
        avgPerOrder,
      ];
    });

    const totalRow = [
      `รวมทั้งสิ้น (${salesTrendData.length} วัน)`,
      "-",
      dailySalesTotals.orders,
      dailySalesTotals.tickets,
      dailySalesTotals.income,
      "100.0%",
      dailySalesTotals.orders > 0
        ? Math.round(dailySalesTotals.income / dailySalesTotals.orders)
        : 0,
    ];

    const ws = xlsx.utils.aoa_to_sheet([...metaRows, headers, ...rows, totalRow]);
    ws["!cols"] = [
      { wch: 24 },
      { wch: 14 },
      { wch: 24 },
      { wch: 18 },
      { wch: 20 },
      { wch: 18 },
      { wch: 24 },
    ];

    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "ยอดขายรายวัน");

    const roundClean = (roundDate || configs?.roundDate || "").replace(/[/\\]/g, "-");
    logReportDownload();
    xlsx.writeFile(
      wb,
      `รายงานสรุปยอดขายรายวัน_งวด_${roundClean}_วันเวลาที่ออกรายงาน_${fileDate}_เวลา_${fileTime}น.xlsx`
    );
  };

  // 4. Coupon Sales Stats
  const couponSalesStats = useMemo(() => {
    return (
      rawDashboardData?.couponSalesStats || {
        total: { orders: 0, amount: 0 },
        approved: { orders: 0, amount: 0 },
        pending: { orders: 0, amount: 0 },
        canceled: { orders: 0, amount: 0 },
        byType: {},
        byTypeArray: [],
        topCouponsArray: [],
      }
    );
  }, [rawDashboardData]);

  const formatDateTime = (val) => {
    if (!val) return "-";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return "-";
      return `${d.toLocaleDateString("th-TH", { day: "numeric", month: "short", year: "2-digit" })} ${d.toLocaleTimeString("th-TH", { hour: "2-digit", minute: "2-digit" })} น.`;
    } catch {
      return "-";
    }
  };

  return (
    <div className="h-fit w-full min-w-[1024px] max-w-[1600px] mx-auto pb-12">
      {/* Top Header */}
      <Header
        header="Dashboard"
        subHeader={
          <div className="flex items-center gap-2.5">
            <span className="text-xs text-slate-500 font-medium">
              แดชบอร์ดสรุปยอดขายและคำสั่งซื้อ {roundDate ? `งวด ${roundDateToDate(roundDate)}` : ""}
            </span>
            {lastUpdated && (
              <span className="text-xs text-slate-400 font-normal">
                (อัปเดตล่าสุด {lastUpdated.toLocaleTimeString("th-TH")} น.)
              </span>
            )}
          </div>
        }
        icon={<RiFileList3Line className="text-blue-600" />}
      />

      {/* ── Realtime Scheduled Tasks Banner (Universal Queue) ── */}
      <ScheduledTasksWidget variant="dashboard" className="mt-2.5 mb-3" />

      {/* Unified System, Round & Status Bar (Prominent Round Header) */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-4 sm:p-5 rounded-md shadow-xs border border-slate-700/60 mt-2 mb-4">
        {/* Row 1: Prominent Active Round & Round Selector */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Left: Round Selector, Status Badges & Title */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-4">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-black text-amber-400 uppercase tracking-widest bg-amber-400/15 border border-amber-400/30 px-2 py-0.5 rounded-md">
                  งวดประจำวันที่
                </span>
                {roundDate === configs?.roundDate ? (
                  <span className="text-[11px] font-bold text-emerald-300 bg-emerald-500/20 border border-emerald-400/30 px-2 py-0.5 rounded-md">
                    ★ งวดปัจจุบัน
                  </span>
                ) : (
                  <span className="text-[11px] font-bold text-slate-300 bg-slate-700/50 border border-slate-600 px-2 py-0.5 rounded-md">
                    งวดประวัติ
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <select
                  value={roundDate}
                  onChange={(e) => setRoundDate(e.target.value)}
                  disabled={loading || syncing}
                  className="bg-slate-800 text-amber-300 font-black text-xl sm:text-2xl lg:text-3xl border-2 border-slate-600 hover:border-amber-400/70 rounded-md px-3 py-1.5 focus:outline-none focus:border-amber-400 cursor-pointer transition shadow-inner disabled:opacity-50"
                >
                  {(configs?.roundDates || []).map((rd) => (
                    <option key={rd} value={rd} className="bg-slate-900 text-white font-medium text-sm sm:text-base">
                      {roundDateToDate(rd)} ({rd}) {rd === configs?.roundDate ? "★ งวดปัจจุบัน" : ""}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Right: Refresh Button */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={handleRecalculate}
              disabled={syncing || loading}
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-bold px-3 py-2 rounded-md border border-white/15 transition shadow-sm disabled:opacity-50"
              title="คำนวณและดึงข้อมูลสรุปใหม่จากฐานข้อมูล"
            >
              <FiRefreshCw className={syncing || loading ? "animate-spin text-base text-cyan-400" : "text-base text-slate-300"} />
              <span className="text-xs font-semibold">รีเฟรชข้อมูล</span>
            </button>
          </div>
        </div>

        {listenerError && (
          <p role="alert" className="mt-3 text-sm text-rose-300">{listenerError}</p>
        )}

        {/* Row 2: Price, Fee, System Status & Closing Time Badges */}
        <div className="flex flex-wrap items-center gap-2 pt-2.5 mt-2.5 text-xs">
          {/* Price */}
          <div className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-md border border-white/10 backdrop-blur-sm">
            <FiTag className="text-emerald-400 text-xs" />
            <span className="text-slate-300">ราคา:</span>
            <span className="font-extrabold text-emerald-300">
              {configs?.price || 80} ฿
            </span>
            {configs?.currency?.lak && (
              <span className="text-[10px] text-slate-400 font-normal">
                ({Number(configs.currency.lak).toLocaleString()} กีบ)
              </span>
            )}
          </div>

          {/* Fee */}
          <div className="flex items-center gap-1.5 bg-white/10 px-2.5 py-1 rounded-md border border-white/10 backdrop-blur-sm">
            <FiDollarSign className="text-amber-400 text-xs" />
            <span className="text-slate-300">ค่าบริการ:</span>
            <span className="font-extrabold text-amber-300">
              {configs?.fee !== undefined ? configs.fee : 0} ฿
            </span>
          </div>

          {/* หน้าร้าน (เลือกใส่ตะกร้า) */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border transition ${configs?.isOpen?.store
              ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/40"
              : "bg-rose-500/20 text-rose-300 border-rose-400/40"
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${configs?.isOpen?.store ? "bg-emerald-400 animate-pulse" : "bg-rose-400"
                }`}
            ></span>
            <span>หน้าร้าน: {configs?.isOpen?.store ? "เปิด" : "ปิด"}</span>
          </div>

          {/* สร้างคำสั่งซื้อ */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border transition ${configs?.isOpen?.createOrder
              ? "bg-teal-500/20 text-teal-300 border-teal-400/40"
              : "bg-rose-500/20 text-rose-300 border-rose-400/40"
              }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${configs?.isOpen?.createOrder ? "bg-teal-400 animate-pulse" : "bg-rose-400"
                }`}
            ></span>
            <span>สร้างคำสั่งซื้อ: {configs?.isOpen?.createOrder ? "เปิด" : "ปิด"}</span>
          </div>

          {/* ตรวจ/ประกาศรางวัล */}
          <div
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${configs?.isAwardReady
              ? "bg-amber-500/20 text-amber-300 border-amber-400/40"
              : "bg-slate-800/80 text-slate-400 border-slate-700/60"
              }`}
          >
            <FiAward className="text-xs text-amber-400" />
            <span>ผลรางวัล: {configs?.isAwardReady ? "ประกาศแล้ว" : "รอออกรางวัล"}</span>
          </div>

          {/* กำหนดเวลาปิดร้าน */}
          {(configs?.closeRoundDateTime || configs?.closeCreateOrderDateTime) && (
            <div className="flex items-center gap-1.5 bg-rose-950/80 border border-rose-700/80 px-2.5 py-1 rounded-md text-xs text-rose-300">
              <FiClock className="text-rose-400 text-xs animate-pulse" />
              <span className="text-slate-300">ปิดร้าน:</span>
              <span className="font-bold text-rose-300">
                {formatDateTime(configs.closeRoundDateTime || configs.closeCreateOrderDateTime)}
              </span>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="h-64 flex flex-col justify-center items-center gap-3">
          <Loading className="w-8 h-8 text-blue-600" />
          <p className="text-sm font-semibold text-gray-500">กำลังเชื่อมต่อข้อมูลเรียลไทม์...</p>
        </div>
      ) : (
        <>
          {/* Ticket Inventory Overview Panel (Light Theme) */}
          <div className="bg-white p-3 rounded-md shadow-sm border border-gray-200 mb-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2 mb-2.5">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center border border-blue-100">
                  <RiFileList3Line className="text-base" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900">
                    สถานะสต็อกสลากประจำงวด
                  </h3>
                  <p className="text-[11px] text-gray-500 font-medium">
                    สรุปจำนวนสลากทั้งหมด
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-white">
              {/* 1. สลากทั้งหมด */}
              <div className="bg-gradient-to-br from-[#475569] to-[#1e293b] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <span className="text-[11px] font-bold text-slate-200 uppercase tracking-wider">สลากทั้งหมด</span>
                  <p className="text-2xl font-black text-white mt-0.5">
                    <AnimatedNumber value={lottoTotal} />
                  </p>
                </div>
              </div>

              {/* 2. On-Block (นำออกขาย) */}
              <div className="bg-gradient-to-br from-[#10b981] to-[#047857] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-emerald-100 uppercase tracking-wider">วางขายในระบบ</span>
                    <span className="text-[10px] font-extrabold text-white bg-white/20 border border-white/30 px-1.5 py-0.2 rounded-md">
                      {calculatePercent(lottoOnBlock, lottoTotal)}
                    </span>
                  </div>
                  <p className="text-2xl font-black text-white mt-0.5">
                    <AnimatedNumber value={lottoOnBlock} />
                  </p>
                </div>
              </div>

              {/* 3. Off-Block (ไม่นำออกขาย/พักไว้) */}
              <div className="bg-gradient-to-br from-[#8b5cf6] to-[#6d28d9] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-purple-100 uppercase tracking-wider">สต๊อกหลังบ้าน</span>
                    <span className="text-[10px] font-extrabold text-white bg-white/20 border border-white/30 px-1.5 py-0.2 rounded-md">
                      {calculatePercent(lottoOffBlock, lottoTotal)}
                    </span>
                  </div>
                  <p className="text-2xl font-black text-white mt-0.5">
                    <AnimatedNumber value={lottoOffBlock} />
                  </p>
                </div>
              </div>

              {/* 4. ขายแล้ว (Sold - Approved) */}
              <div className="bg-gradient-to-br from-[#06b6d4] to-[#0284c7] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-cyan-100 uppercase tracking-wider">ขายแล้ว</span>
                    <span className="text-[10px] font-extrabold text-white bg-white/20 border border-white/30 px-1.5 py-0.2 rounded-md">
                      {calculatePercent(approvedTickets, lottoTotal)}
                    </span>
                  </div>
                  <p className="text-2xl font-black text-white mt-0.5">
                    <AnimatedNumber value={approvedTickets} />
                  </p>
                </div>
              </div>

              {/* 5. คงเหลือพร้อมขาย (Remaining) */}
              <div className="bg-gradient-to-br from-[#f59e0b] to-[#ea580c] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-amber-100 uppercase tracking-wider">คงเหลือ | ตะกร้า</span>
                  </div>
                  <p className="text-2xl font-black text-white mt-0.5 flex items-center gap-1 flex-wrap">
                    <AnimatedNumber value={Math.max(0, lottoRemaining - lottoInCart)} />
                    <span className="text-amber-200 font-normal text-xl">|</span>
                    <AnimatedNumber value={lottoInCart} />
                  </p>
                </div>
              </div>

              {/* 6. ผู้ใช้ทั้งหมด (Total Users) */}
              <div className="bg-gradient-to-br from-[#3b82f6] to-[#4f46e5] p-3 rounded-md shadow-xs border border-white/10 flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-blue-100 uppercase tracking-wider">ผู้ใช้ทั้งหมด</span>
                    {newUsers30DaysTotals.count > 0 && (
                      <span className="text-[10px] font-extrabold text-white bg-white/20 border border-white/30 px-1.5 py-0.2 rounded-md">
                        +{newUsers30DaysTotals.count.toLocaleString()}
                      </span>
                    )}
                  </div>
                  <p className="text-2xl font-black text-white mt-0.5">
                    <AnimatedNumber value={totalUsers} />
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 4 Status Metric Cards + 30-Day New Users Mini Chart Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
              const item = stats?.[key] || { orders: 0, tickets: 0, amount: 0 };
              const Icon = cfg.icon;
              const orderPct = calculatePercent(item.orders, totalOrders);
              const avgPerOrder = item.orders > 0 ? Math.round(item.amount / item.orders) : 0;

              return (
                <div
                  key={key}
                  className={`bg-white rounded-md p-3 border ${cfg.border} shadow-sm hover:shadow-md transition duration-200 flex flex-col justify-between`}
                >
                  <div>
                    {/* Header of Card */}
                    <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md ${cfg.lightBg}`}>
                          <Icon className={`text-base ${cfg.textColor}`} />
                        </div>
                        <div>
                          <p className="font-bold text-gray-800 text-xs">{cfg.label}</p>
                          <p className="text-[10px] text-gray-400">{cfg.subLabel}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold px-1.5 py-0.2 rounded-md border ${cfg.badgeColor}`}>
                        {orderPct}
                      </span>
                    </div>
                    <div className="mt-2.5 space-y-2">
                      <div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11px] font-semibold text-gray-500">จำนวนคำสั่งซื้อ:</span>
                          <span className="text-base font-bold text-gray-900">
                            <AnimatedNumber value={item.orders} /> <span className="text-[10px] font-normal text-gray-400">คำสั่งซื้อ</span>
                          </span>
                        </div>
                      </div>

                      <div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11px] font-semibold text-gray-500">จำนวนสลาก:</span>
                          <span className="text-sm font-bold text-gray-900">
                            <AnimatedNumber value={item.tickets} /> <span className="text-[10px] font-normal text-gray-400">ใบ</span>
                          </span>
                        </div>
                      </div>

                      {key !== "canceled" && <div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-[11px] font-semibold text-gray-500">ยอดเงินรวม:</span>
                          <span className={`text-base font-extrabold ${cfg.textColor}`}>
                            <AnimatedNumber value={item.amount} prefix="฿" />
                          </span>
                        </div>
                      </div>}
                    </div>
                  </div>

                  {/* Footer Avg */}
                  {key !== "canceled" && <div className="mt-2.5 pt-2 border-t border-gray-100 flex justify-between items-center text-[11px] text-gray-500">
                    <span>เฉลี่ย/คำสั่งซื้อ:</span>
                    <span className="font-semibold text-gray-700"><AnimatedNumber value={avgPerOrder} prefix="฿" /></span>
                  </div>}
                </div>
              );
            })}

            {/* 5th Card: New Users 30 Days Mini Chart (ข้างคำสั่งซื้อ ยกเลิก) */}
            <div className="bg-white rounded-md p-3 border border-indigo-200/80 shadow-sm hover:shadow-md transition duration-200 flex flex-col justify-between">
              <div>
                {/* Header of Card */}
                <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 rounded-md bg-indigo-50 text-indigo-600 border border-indigo-100">
                      <FiUsers className="text-base" />
                    </div>
                    <div>
                      <p className="font-bold text-gray-800 text-xs">ผู้ใช้ใหม่ 30 วัน</p>
                      <p className="text-[10px] text-gray-400">สรุปย้อนหลัง 30 วัน</p>
                    </div>
                  </div>
                  {/* <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md border bg-indigo-50 text-indigo-800 border-indigo-200">
                    {newUsers30DaysData.length} วัน
                  </span> */}
                </div>

                {/* Quick KPI stats */}
                <div className="mt-2">
                  <span className="text-[11px] font-semibold text-gray-500">ผู้ใช้ใหม่รวม:</span>
                  <p className="text-base font-black text-indigo-600 leading-tight">
                    +{newUsers30DaysTotals.count.toLocaleString()}{" "}
                    <span className="text-[10px] font-normal text-gray-400">คน</span>
                  </p>
                </div>

                {/* Mini Bar Chart */}
                <div className="h-14 w-full mt-1">
                  {newUsers30DaysData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={newUsers30DaysData} margin={{ top: 2, right: 1, left: 1, bottom: 0 }}>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const d = payload[0].payload;
                              return (
                                <div className="bg-slate-900/95 text-white text-[11px] px-2 py-1.5 rounded-md shadow-lg border border-slate-700 pointer-events-none z-50">
                                  <p className="font-bold text-slate-200">{formatThaiDate(d.fullDate)}</p>
                                  <p className="text-indigo-300 font-extrabold mt-0.5">
                                    +{d.count?.toLocaleString()} คน
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          }}
                          cursor={{ fill: "rgba(99, 102, 241, 0.12)" }}
                        />
                        <Bar
                          dataKey="count"
                          fill="#6366f1"
                          radius={[2, 2, 0, 0]}
                          maxBarSize={6}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                      ไม่มีข้อมูล
                    </div>
                  )}
                </div>
              </div>

              {/* Footer Peak */}
              <div className="mt-2 pt-2 border-t border-gray-100 flex justify-between items-center text-[11px] text-gray-500">
                <span>สูงสุด/วัน:</span>
                <span className="font-semibold text-amber-600">
                  {maxDailyNewUsers > 0 ? `⭐ ${maxDailyNewUsers.toLocaleString()} คน` : "-"}
                </span>
              </div>
            </div>
          </div>

          {/* ═══════════════════════════════════════════════════════════════════ */}
          {/* Daily Sales Table (ตารางสรุปยอดขายรายวัน) */}
          <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden mb-3">
            {/* Table Header & Quick KPI Badges */}
            <div className="px-4 py-3 bg-white border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600 shadow-sm">
                  <FiTrendingUp className="text-base" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900">
                    ตารางสรุปยอดขายรายวัน
                  </h3>
                  <p className="text-[11px] text-gray-500 font-medium">
                    สรุปประวัติยอดขาย คำสั่งซื้อ และจำนวนสลากแยกตามแต่ละวันในงวด
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {salesTrendData.length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="px-2.5 py-1 rounded-md bg-slate-50 text-slate-700 border border-slate-200 font-semibold flex items-center gap-1">
                      <FiCalendar className="text-slate-500" />
                      <span>{salesTrendData.length} วัน</span>
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold">
                      ยอดรวม ฿{dailySalesTotals.income.toLocaleString()}
                    </span>
                    <span className="px-2.5 py-1 rounded-md bg-sky-50 text-sky-800 border border-sky-200 font-semibold">
                      {dailySalesTotals.tickets.toLocaleString()} ใบ
                    </span>
                  </div>
                )}

                {salesTrendData.length > 0 && (
                  <button
                    onClick={exportDailySalesToXLSX}
                    className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-3 py-1.5 rounded-md transition shadow-xs disabled:opacity-50"
                    title="ดาวน์โหลดรายงานสรุปยอดขายรายวันเป็นไฟล์ Excel (.xlsx)"
                  >
                    <FiDownload className="text-sm" />
                    <span>ดาวน์โหลด Excel (.xlsx)</span>
                  </button>
                )}
              </div>
            </div>

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/90 border-b border-gray-200">
                    <TableHead className="font-bold text-slate-700 text-xs py-2.5">วันที่ทำรายการ</TableHead>
                    <TableHead className="text-right font-bold text-slate-700 text-xs py-2.5">จำนวนคำสั่งซื้อ</TableHead>
                    <TableHead className="text-right font-bold text-slate-700 text-xs py-2.5">จำนวนสลาก (ใบ)</TableHead>
                    <TableHead className="text-right font-bold text-slate-700 text-xs py-2.5">ยอดขายรวม (บาท)</TableHead>
                    <TableHead className="text-right font-bold text-slate-700 text-xs py-2.5">สัดส่วนยอดขาย (%)</TableHead>
                    <TableHead className="text-right font-bold text-slate-700 text-xs py-2.5">เฉลี่ย / คำสั่งซื้อ</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesTrendData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-gray-400">
                        <FiCalendar className="text-3xl mx-auto mb-2 opacity-30 text-gray-400" />
                        <p className="text-xs font-semibold text-gray-500">ยังไม่มีข้อมูลยอดขายรายวันสำหรับงวดนี้</p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {salesTrendData.map((item, idx) => {
                        const percent =
                          dailySalesTotals.income > 0
                            ? ((item.income / dailySalesTotals.income) * 100).toFixed(1)
                            : "0.0";
                        const avgPerOrder =
                          item.orders > 0 ? Math.round(item.income / item.orders) : 0;
                        const isPeak = maxDailyIncome > 0 && item.income === maxDailyIncome;

                        return (
                          <TableRow
                            key={item.fullDate || idx}
                            className={`hover:bg-slate-50/80 transition duration-150 ${isPeak ? "bg-amber-50/30" : ""
                              }`}
                          >
                            <TableCell className="py-2.5">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`w-2 h-2 rounded-full ${isPeak ? "bg-amber-500 ring-2 ring-amber-200" : "bg-emerald-500"
                                    }`}
                                />
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="text-gray-900 font-bold text-xs">
                                    {formatThaiDate(item.fullDate)}
                                  </span>
                                  <span className="text-[11px] text-gray-400 font-normal">
                                    ({item.fullDate})
                                  </span>
                                  {isPeak && (
                                    <span className="text-[10px] font-bold px-1.5 py-0.2 rounded-md bg-amber-100 text-amber-800 border border-amber-300">
                                      ⭐ ยอดขายสูงสุด
                                    </span>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right py-2.5 font-bold text-gray-800 text-xs">
                              {item.orders.toLocaleString()}{" "}
                              <span className="text-[11px] font-normal text-gray-400">รายการ</span>
                            </TableCell>
                            <TableCell className="text-right py-2.5 font-bold text-emerald-600 text-xs">
                              {item.tickets.toLocaleString()}{" "}
                              <span className="text-[11px] font-normal text-emerald-600/70">ใบ</span>
                            </TableCell>
                            <TableCell className="text-right py-2.5 font-extrabold text-gray-900 text-xs">
                              ฿{item.income.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right py-2.5">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 bg-gray-200/80 rounded-full h-1.5 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all duration-300 ${isPeak ? "bg-amber-500" : "bg-emerald-500"
                                      }`}
                                    style={{
                                      width: `${Math.min(100, Math.max(0, Number(percent)))}%`,
                                    }}
                                  />
                                </div>
                                <span className="text-xs font-bold text-gray-700 min-w-[42px] text-right">
                                  {percent}%
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right py-2.5 font-semibold text-gray-600 text-xs">
                              ฿{avgPerOrder.toLocaleString()}
                            </TableCell>
                          </TableRow>
                        );
                      })}

                      {/* Summary / Total Row */}
                      <TableRow className="bg-slate-100/90 font-bold border-t-2 border-slate-300">
                        <TableCell className="text-gray-900 font-extrabold text-xs py-3">
                          รวมทั้งสิ้น ({salesTrendData.length} วัน)
                        </TableCell>
                        <TableCell className="text-right text-gray-900 font-extrabold text-xs py-3">
                          {dailySalesTotals.orders.toLocaleString()}{" "}
                          <span className="text-[11px] font-normal text-gray-500">รายการ</span>
                        </TableCell>
                        <TableCell className="text-right text-emerald-700 font-extrabold text-xs py-3">
                          {dailySalesTotals.tickets.toLocaleString()}{" "}
                          <span className="text-[11px] font-normal text-emerald-700/70">ใบ</span>
                        </TableCell>
                        <TableCell className="text-right text-emerald-700 font-extrabold text-sm py-3">
                          ฿{dailySalesTotals.income.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right text-gray-900 font-extrabold text-xs py-3">
                          100.0%
                        </TableCell>
                        <TableCell className="text-right text-gray-800 font-extrabold text-xs py-3">
                          ฿
                          {dailySalesTotals.orders > 0
                            ? Math.round(
                              dailySalesTotals.income / dailySalesTotals.orders
                            ).toLocaleString()
                            : "0"}
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>



          {/* Top 3 Channel Highlight Cards (Approved Metrics) */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3 text-white">
            {/* Web Orders Card */}
            <div className="bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] rounded-md p-3.5 shadow-xs border border-white/15 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-white/15 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-white/20 flex items-center justify-center text-white font-bold text-sm">
                      <FiGlobe className="text-base" />
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-white flex items-center gap-2">
                        คำสั่งซื้อเว็บไซต์ / ทั่วไป
                      </h3>
                      <p className="text-[10px] text-blue-100 font-medium">เฉพาะอนุมัติแล้ว</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/20 text-white border border-white/30">
                    เว็บทั่วไป
                  </span>
                </div>

                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-blue-100 truncate">คำสั่งซื้อ</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={webApprovedOrders} /> <span className="text-[9px] font-normal text-white/80">รายการ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-blue-100 truncate">จำนวนสลาก</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={webApprovedTickets} /> <span className="text-[9px] font-normal text-white/80">ใบ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-blue-100 truncate">ยอดเงินรวม</p>
                    <p className="text-sm font-black text-amber-200 mt-0.5 truncate">
                      <AnimatedNumber value={webApprovedAmount} prefix="฿" />
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-2.5 pt-2 border-t border-white/15 flex items-center justify-between text-[11px] text-blue-100">
                <span>
                  สัดส่วนสลาก: <strong className="text-white">{calculatePercent(webApprovedTickets, approvedTickets)}</strong>
                </span>
                <span>
                  สัดส่วนคำสั่งซื้อ: <strong className="text-white">{calculatePercent(webApprovedOrders, approvedOrders)}</strong>
                </span>
              </div>
            </div>

            {/* LINE App Orders Card */}
            <div className="bg-gradient-to-br from-[#06C755] to-[#049641] rounded-md p-3.5 shadow-xs border border-white/15 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-white/15 pb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-md bg-white text-[#049641] font-black text-xs flex items-center justify-center shadow-xs">
                      LINE
                    </div>
                    <div>
                      <h3 className="font-bold text-sm text-white flex items-center gap-2">
                        คำสั่งซื้อจาก LINE App
                      </h3>
                      <p className="text-[10px] text-emerald-100 font-medium">เฉพาะอนุมัติแล้ว</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/20 text-white border border-white/30">
                    LINE
                  </span>
                </div>

                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-emerald-100 truncate">คำสั่งซื้อ</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={lineApprovedOrders} /> <span className="text-[9px] font-normal text-white/80">รายการ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-emerald-100 truncate">จำนวนสลาก</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={lineApprovedTickets} /> <span className="text-[9px] font-normal text-white/80">ใบ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-emerald-100 truncate">ยอดเงินรวม</p>
                    <p className="text-sm font-black text-amber-200 mt-0.5 truncate">
                      <AnimatedNumber value={lineApprovedAmount} prefix="฿" />
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-2.5 pt-2 border-t border-white/15 flex items-center justify-between text-[11px] text-emerald-100">
                <span>
                  สัดส่วนสลาก: <strong className="text-white">{calculatePercent(lineApprovedTickets, approvedTickets)}</strong>
                </span>
                <span>
                  สัดส่วนคำสั่งซื้อ: <strong className="text-white">{calculatePercent(lineApprovedOrders, approvedOrders)}</strong>
                </span>
              </div>
            </div>

            {/* Lao Customer Card */}
            <div className="bg-gradient-to-br from-[#6366f1] to-[#4338ca] rounded-md p-3.5 shadow-xs border border-white/15 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between border-b border-white/15 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">🇱🇦</span>
                    <div>
                      <h3 className="font-bold text-sm text-white flex items-center gap-2">
                        คำสั่งซื้อลูกค้า สปป.ลาว
                      </h3>
                      <p className="text-[10px] text-indigo-100 font-medium">เฉพาะอนุมัติแล้ว</p>
                    </div>
                  </div>
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-white/20 text-white border border-white/30">
                    สปป.ลาว
                  </span>
                </div>

                <div className="mt-2.5 grid grid-cols-3 gap-2">
                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-indigo-100 truncate">คำสั่งซื้อ</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={laoApprovedOrders} /> <span className="text-[9px] font-normal text-white/80">รายการ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-indigo-100 truncate">จำนวนสลาก</p>
                    <p className="text-sm font-black text-white mt-0.5 truncate">
                      <AnimatedNumber value={laoApprovedTickets} /> <span className="text-[9px] font-normal text-white/80">ใบ</span>
                    </p>
                  </div>

                  <div className="bg-white/15 rounded-md p-2 border border-white/10 min-w-0 flex flex-col justify-between overflow-hidden">
                    <p className="text-[10px] font-semibold text-indigo-100 truncate">ยอดเงินรวม</p>
                    <p className="text-sm font-black text-amber-200 mt-0.5 truncate">
                      <AnimatedNumber value={laoApprovedAmountLak} prefix="₭" />
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-2.5 pt-2 border-t border-white/15 flex items-center justify-between text-[11px] text-indigo-100">
                <span>
                  สัดส่วนสลาก: <strong className="text-white">{calculatePercent(laoApprovedTickets, approvedTickets)}</strong>
                </span>
                <span>
                  สัดส่วนคำสั่งซื้อ: <strong className="text-white">{calculatePercent(laoApprovedOrders, approvedOrders)}</strong>
                </span>
              </div>
            </div>
          </div>

          {/* ─── Last Two Digits (3/4) & Lottery Set (1/4) Stock Analytics ─── */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-3 mb-3">
            {/* Column 1 (3/4): Last Two Digits Stock Analytics */}
            <div className="xl:col-span-3">
              <LastTwoDigitsStockPanel
                currentLottoLastTwo={rawDashboardData?.currentLottoLastTwo}
                roundDate={roundDate}
                limitLow={40}
                limitHigh={20}
                className="mt-0 h-full"
              />
            </div>

            {/* Column 2 (1/4): Lottery Set Stock Breakdown */}
            <div className="xl:col-span-1">
              <LotteryBlockSetsPanel
                currentBlocks={rawDashboardData?.currentBlocks}
                className="h-full"
              />
            </div>
          </div>

          {/* Coupon Sales Highlight Banner (Light Theme) */}
          <div className="bg-white rounded-md p-3.5 shadow-sm border border-amber-200/80 relative overflow-hidden mb-3">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-2.5 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-600 shadow-sm">
                  <RiCoupon2Line className="text-lg" />
                </div>
                <div>
                  <h3 className="font-bold text-sm text-gray-900">
                    สรุปยอดขายคูปองและแพ็กเกจประจำงวด
                  </h3>
                  <p className="text-[11px] text-gray-500 font-medium">
                    ข้อมูลรายได้และคำสั่งซื้อคูปอง Voucher, ส่วนลด, สิทธิ์จอง และ VIP
                  </p>
                </div>
              </div>
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200">
                คูปองและแพ็กเกจ
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
              <div className="bg-amber-50/70 rounded-md p-3 border border-amber-200/70">
                <p className="text-[11px] font-semibold text-amber-800">ยอดขายคูปองที่อนุมัติแล้ว</p>
                <p className="text-xl font-extrabold text-amber-600 mt-0.5">
                  ฿{(couponSalesStats?.approved?.amount || 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-amber-700/80 mt-0.5 font-medium">
                  {(couponSalesStats?.approved?.orders || 0).toLocaleString()} คำสั่งซื้ออนุมัติ
                </p>
              </div>

              <div className="bg-slate-50 rounded-md p-3 border border-slate-200">
                <p className="text-[11px] font-semibold text-gray-600">คำสั่งซื้อคูปองทั้งหมด</p>
                <p className="text-xl font-extrabold text-gray-900 mt-0.5">
                  {(couponSalesStats?.total?.orders || 0).toLocaleString()}{" "}
                  <span className="text-[11px] font-normal text-gray-500">รายการ</span>
                </p>
                <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">
                  อนุมัติแล้ว: {(couponSalesStats?.approved?.orders || 0).toLocaleString()} รายการ
                </p>
              </div>

              <div className="bg-sky-50/70 rounded-md p-3 border border-sky-200/70">
                <p className="text-[11px] font-semibold text-sky-800">รอชำระ / รอดำเนินการ</p>
                <p className="text-xl font-extrabold text-sky-600 mt-0.5">
                  {(couponSalesStats?.pending?.orders || 0).toLocaleString()}{" "}
                  <span className="text-[11px] font-normal text-sky-500">รายการ</span>
                </p>
                <p className="text-[10px] text-sky-700/80 mt-0.5 font-medium">
                  มูลค่า: ฿{(couponSalesStats?.pending?.amount || 0).toLocaleString()}
                </p>
              </div>

              <div className="bg-purple-50/70 rounded-md p-3 border border-purple-200/70">
                <p className="text-[11px] font-semibold text-purple-800">ประเภทคูปองที่มียอดขาย</p>
                <p className="text-xl font-extrabold text-purple-600 mt-0.5">
                  {(couponSalesStats?.byTypeArray?.length || 0)}{" "}
                  <span className="text-[11px] font-normal text-purple-500">ประเภท</span>
                </p>
                <p className="text-[10px] text-purple-700/80 mt-0.5 font-medium">
                  แพ็กเกจ/รายการ: {(couponSalesStats?.topCouponsArray?.length || 0)} รายการ
                </p>
              </div>
            </div>
          </div>



          {/* Channel Comparison Table */}
          <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden mb-3">
            <div className="px-3.5 py-2.5 bg-white border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiLayers className="text-indigo-600 text-base" />
                <h3 className="font-bold text-sm text-gray-800">
                  ตารางเปรียบเทียบคำสั่งซื้อแยกตามช่องทาง
                </h3>
              </div>
              <span className="text-[11px] text-gray-500 font-medium">สถิติลูกค้าลาว, LINE App และเว็บทั่วไป</span>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100/80">
                  <TableHead className="font-bold text-gray-700">ช่องทางการสั่งซื้อ</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">คำสั่งซื้อทั้งหมด</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">คำสั่งซื้ออนุมัติแล้ว</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">จำนวนสลาก (ใบ)</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">สลากอนุมัติแล้ว (ใบ)</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">ยอดเงินรวม</TableHead>
                  {/* <TableHead className="text-right font-bold text-gray-700">ยอดเงินอนุมัติแล้ว</TableHead> */}
                  <TableHead className="text-right font-bold text-gray-700">สัดส่วนสลาก (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Web / General Row */}
                <TableRow className="hover:bg-gray-50/50 transition">
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      <FiGlobe className="text-blue-500" />
                      <span className="text-gray-900 font-bold">ช่องทางทั่วไป / เว็บไซต์</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {webOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {webApprovedOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {webTickets.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {webApprovedTickets.toLocaleString()}
                  </TableCell>
                  {/* <TableCell className="text-right font-extrabold text-blue-600">
                    ฿{webAmount.toLocaleString()}
                  </TableCell> */}
                  <TableCell className="text-right font-bold text-emerald-700">
                    ฿{webApprovedAmount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-gray-600">
                    {calculatePercent(webTickets, totalTickets)}
                  </TableCell>
                </TableRow>

                {/* LINE App Row */}
                <TableRow className="hover:bg-emerald-50/50 transition">
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 rounded-md bg-[#06C755] flex items-center justify-center text-white font-bold text-[9px]">
                        L
                      </div>
                      <span className="text-gray-900 font-bold">คำสั่งซื้อ LINE</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {lineOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {lineApprovedOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {lineTickets.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {lineApprovedTickets.toLocaleString()}
                  </TableCell>
                  {/* <TableCell className="text-right font-extrabold text-[#06C755]">
                    ฿{lineAmount.toLocaleString()}
                  </TableCell> */}
                  <TableCell className="text-right font-bold text-emerald-700">
                    ฿{lineApprovedAmount.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-gray-600">
                    {calculatePercent(lineTickets, totalTickets)}
                  </TableCell>
                </TableRow>
                {/* Lao Row */}
                <TableRow className="hover:bg-blue-50/50 transition">
                  <TableCell className="font-semibold">
                    <div className="flex items-center gap-2">
                      <span className="text-base">🇱🇦</span>
                      <span className="text-gray-900 font-bold">ลูกค้า สปป.ลาว</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {laoOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {laoApprovedOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    {laoTickets.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-emerald-600">
                    {laoApprovedTickets.toLocaleString()}
                  </TableCell>
                  {/* <TableCell className="text-right font-extrabold text-indigo-600">
                    ₭{laoAmountLak.toLocaleString()}
                  </TableCell> */}
                  <TableCell className="text-right font-bold text-emerald-700">
                    ₭{laoApprovedAmountLak.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-semibold text-gray-600">
                    {calculatePercent(laoTickets, totalTickets)}
                  </TableCell>
                </TableRow>


                {/* Total Row */}
                <TableRow className="bg-slate-100 font-bold border-t-2 border-gray-300">
                  <TableCell className="font-extrabold text-gray-900 text-sm">
                    รวมทุกช่องทาง
                  </TableCell>
                  <TableCell className="text-right font-extrabold text-gray-900 text-sm">
                    {totalOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">
                    {(stats?.approved?.orders || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-extrabold text-gray-900 text-sm">
                    {totalTickets.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-emerald-700">
                    {(stats?.approved?.tickets || 0).toLocaleString()}
                  </TableCell>
                  {/* <TableCell className="text-right font-extrabold text-blue-700 text-sm">
                    ฿{totalAmount.toLocaleString()}
                  </TableCell> */}
                  <TableCell className="text-right font-extrabold text-emerald-700 text-sm">
                    ฿{(stats?.approved?.amount || 0).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-700">100.0%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Coupon Sales Breakdown Table */}
          {couponSalesStats?.byTypeArray && couponSalesStats.byTypeArray.length > 0 && (
            <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden mb-3">
              <div className="px-3.5 py-2.5 bg-white border-b flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <RiCoupon2Line className="text-amber-600 text-base" />
                  <h3 className="font-bold text-sm text-gray-800">
                    ตารางสรุปยอดขายคูปองแยกตามประเภท
                  </h3>
                </div>
                <span className="text-[11px] text-amber-900 font-semibold">
                  รวม {couponSalesStats.total?.orders?.toLocaleString()} รายการ | ฿{couponSalesStats.total?.amount?.toLocaleString()} บาท
                </span>
              </div>

              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-100/80">
                    <TableHead className="font-bold text-gray-700">ประเภทคูปอง</TableHead>
                    <TableHead className="text-right font-bold text-gray-700">คำสั่งซื้อทั้งหมด</TableHead>
                    <TableHead className="text-right font-bold text-gray-700">คำสั่งซื้ออนุมัติแล้ว</TableHead>
                    <TableHead className="text-right font-bold text-gray-700">ยอดขายรวม (บาท)</TableHead>

                    <TableHead className="text-right font-bold text-gray-700">สัดส่วนยอดขาย (%)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {couponSalesStats.byTypeArray.map((item) => {
                    const cfg = COUPON_TYPE_CONFIG[item.type] || COUPON_TYPE_CONFIG.other;
                    const pct = calculatePercent(item.amount, couponSalesStats.total?.amount || 1);

                    return (
                      <TableRow key={item.type} className="hover:bg-amber-50/40 transition">
                        <TableCell className="font-semibold">
                          <div className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: cfg.color }}></span>
                            <span className="text-gray-900 font-bold">{cfg.label}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-bold text-gray-800">
                          {item.orders.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-emerald-600">
                          {item.approvedOrders.toLocaleString()}
                        </TableCell>
                        {/* <TableCell className="text-right font-extrabold text-amber-600">
                          ฿{item.amount.toLocaleString()}
                        </TableCell> */}
                        <TableCell className="text-right font-bold text-emerald-700">
                          ฿{item.approvedAmount.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-gray-600">
                          {pct}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Total Row */}
                  <TableRow className="bg-slate-50/80 font-bold border-t-2 border-slate-300">
                    <TableCell className="font-extrabold text-gray-900 text-sm">
                      รวมยอดขายคูปองทั้งหมด
                    </TableCell>
                    <TableCell className="text-right font-extrabold text-gray-900 text-sm">
                      {couponSalesStats.total?.orders?.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-bold text-emerald-700">
                      {couponSalesStats.approved?.orders?.toLocaleString()}
                    </TableCell>
                    {/* <TableCell className="text-right font-extrabold text-amber-700 text-base">
                      ฿{couponSalesStats.total?.amount?.toLocaleString()}
                    </TableCell> */}
                    <TableCell className="text-right font-extrabold text-emerald-700 text-base">
                      ฿{couponSalesStats.approved?.amount?.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right font-bold text-gray-700">100.0%</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}

          {/* Detailed Status Breakdown Table */}
          <div className="bg-white rounded-md shadow-sm border border-gray-200 overflow-hidden mb-3">
            <div className="px-3.5 py-2.5 bg-white border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FiActivity className="text-blue-600" />
                <h3 className="font-bold text-sm text-gray-800">ตารางสรุปสัดส่วนและเปรียบเทียบตามสถานะคำสั่งซื้อ</h3>
              </div>
              <span className="text-[11px] text-gray-500 font-medium">รวม 4 สถานะหลัก</span>
            </div>

            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100/70">
                  <TableHead className="font-bold text-gray-700">สถานะคำสั่งซื้อ</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">จำนวนคำสั่งซื้อ</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">สัดส่วนคำสั่งซื้อ</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">จำนวนสลาก (ใบ)</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">สัดส่วนสลาก</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">ยอดเงินรวม (บาท)</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">สัดส่วนเงิน</TableHead>
                  <TableHead className="text-right font-bold text-gray-700">เฉลี่ย/คำสั่งซื้อ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                  const item = stats?.[key] || { orders: 0, tickets: 0, amount: 0 };
                  const Icon = cfg.icon;
                  const orderPct = calculatePercent(item.orders, totalOrders);
                  const ticketPct = calculatePercent(item.tickets, totalTickets);
                  const amountPct = calculatePercent(item.amount, totalAmount);
                  const avgPerOrder = item.orders > 0 ? Math.round(item.amount / item.orders) : 0;

                  return (
                    <TableRow key={key} className="hover:bg-gray-50/80 transition">
                      <TableCell className="font-semibold">
                        <div className="flex items-center gap-2">
                          <Icon className={`text-base ${cfg.textColor}`} />
                          <span className="text-gray-800">{cfg.label}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold text-gray-800">
                        {item.orders.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-600">
                        {orderPct}
                      </TableCell>
                      <TableCell className="text-right font-bold text-gray-800">
                        {item.tickets.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-600">
                        {ticketPct}
                      </TableCell>
                      <TableCell className={`text-right font-extrabold ${cfg.textColor}`}>
                        ฿{item.amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-600">
                        {amountPct}
                      </TableCell>
                      <TableCell className="text-right font-semibold text-gray-700">
                        ฿{avgPerOrder.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {/* Total Row */}
                <TableRow className="bg-gray-50 font-bold border-t-2 border-gray-300">
                  <TableCell className="font-extrabold text-gray-900 text-sm">
                    รวมทั้งหมด
                  </TableCell>
                  <TableCell className="text-right font-extrabold text-gray-900 text-sm">
                    {totalOrders.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-700">100.0%</TableCell>
                  <TableCell className="text-right font-extrabold text-gray-900 text-sm">
                    {totalTickets.toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-700">100.0%</TableCell>
                  <TableCell className="text-right font-extrabold text-blue-700 text-base">
                    {/* ฿{totalAmount.toLocaleString()} */}
                  </TableCell>
                  <TableCell className="text-right font-bold text-gray-700">100.0%</TableCell>
                  <TableCell className="text-right font-bold text-gray-800">
                    ฿{totalOrders > 0 ? Math.round(totalAmount / totalOrders).toLocaleString() : 0}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default RealtimeOrderDashboard;
